/**
 * apps/web/src/pages/AnalyticsPage.tsx
 * -------------------------------------------------------
 * Purpose:
 *   Dashboard view for high-level audit workload metrics.
 *
 * High-level data flow:
 *   - loader (router.tsx -> analyticsLoader) fetches:
 *       1) `/api/analytics/summary`
 *       2) `/api/audits?filter=flagged&limit=5`
 *   - The page renders those results as simple KPI cards and a short "recent flagged" list.
 */

import React from 'react';
import {
  Card,
  Grid,
  Group,
  Progress,
  SegmentedControl,
  Tooltip,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import {
  Link,
  useLoaderData,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import type { AnalyticsSummary, AuditSummary } from '@tinypro-audit/contracts';
import { formatDateTimeInAppTimeZone } from '@tinypro-audit/shared/time';
import { AuditStatusCell } from '../components/AuditResolutionBadge';
import {
  ANALYTICS_RANGE_OPTIONS,
  getWeekToDateRangeLabel,
  getLast7DaysRangeLabel,
  type AnalyticsRange,
} from './analyticsRanges';

/** Loader data returned by analyticsLoader(). */
type LoaderData = {
  summary: AnalyticsSummary;
  recentAudits: AuditSummary[];
  range: AnalyticsRange;
};

export function AnalyticsPage(): React.ReactElement {
  /** Data provided by the route loader. */
  const { summary, recentAudits, range } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const location = useLocation();

  /** Update the URL range param so loader refetches with new bucket. */
  function handleRangeChange(next: string): void {
    const params = new URLSearchParams(location.search);
    params.set('range', next);
    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    });
  }

  /** Decorate the Week-to-date option with a hover tooltip showing the range. */
  const rangeOptions = React.useMemo(
    () =>
      ANALYTICS_RANGE_OPTIONS.map((opt) =>
        opt.value === 'week-to-date'
          ? {
              ...opt,
              label: (
                <Tooltip label={getWeekToDateRangeLabel()} withArrow>
                  <span>{opt.label}</span>
                </Tooltip>
              ),
            }
          : opt.value === 'last-7-days'
            ? {
                ...opt,
                label: (
                  <Tooltip label={getLast7DaysRangeLabel()} withArrow>
                    <span>{opt.label}</span>
                  </Tooltip>
                ),
              }
            : opt,
      ),
    [],
  );

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>Pre-site form review analytics</Title>
          <Text c="dimmed">
            Track pre-site form reviews and follow-up workload.
          </Text>
        </div>
        <SegmentedControl
          value={range}
          onChange={handleRangeChange}
          data={rangeOptions}
          size="sm"
        />
      </Group>

      <Grid>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card withBorder radius="md">
            <Text size="sm" c="dimmed">
              Total pre-site reviews
            </Text>
            <Title order={3}>{summary.totalAudits}</Title>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card withBorder radius="md">
            <Text size="sm" c="dimmed">
              Flagged
            </Text>
            <Title order={3}>{summary.flaggedCount}</Title>
            <Text size="xs" c="dimmed">
              Pre-site reviews needing follow-up
            </Text>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card withBorder radius="md">
            <Text size="sm" c="dimmed">
              Flagged %
            </Text>
            <Title order={3}>{summary.flaggedPercent}%</Title>
            <Progress value={summary.flaggedPercent} mt="sm" />
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card withBorder radius="md">
            <Text size="sm" c="dimmed">
              Top issue type
            </Text>
            <Title order={4}>{summary.topIssues[0]?.type ?? '—'}</Title>
            <Text size="xs" c="dimmed">
              {summary.topIssues[0]?.count ?? 0} occurrences
            </Text>
          </Card>
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Card withBorder radius="md">
            <Group justify="space-between" mb="sm">
              <div>
                <Title order={4}>Pre-site reviews in range</Title>
                <Text size="sm" c="dimmed">
                  Showing latest {recentAudits.length} of {summary.totalAudits}{' '}
                  jobs in this range.
                </Text>
              </div>
              <Text component={Link} to="/projects/pre-site" size="sm">
                View all
              </Text>
            </Group>

            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Job</Table.Th>
                  <Table.Th>Submitted</Table.Th>
                  <Table.Th>Technician</Table.Th>
                  <Table.Th>Location</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {recentAudits.map((c) => (
                  <Table.Tr key={c.id}>
                    <Table.Td>
                      <AuditStatusCell
                        status={c.status}
                        reviewState={c.reviewState}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text component={Link} to={`/audits/${c.id}`} fw={600}>
                        {c.jobId}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {formatDateTimeInAppTimeZone(c.submittedAt)}
                    </Table.Td>
                    <Table.Td>{c.technicianName}</Table.Td>
                    <Table.Td>{c.location}</Table.Td>
                  </Table.Tr>
                ))}
                {recentAudits.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed">No audits found for this range.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Card withBorder radius="md">
            <Title order={4} mb="sm">
              Top flagged issues
            </Title>

            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Issue</Table.Th>
                  <Table.Th ta="right">Count</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {summary.topIssues.map((it) => (
                  <Table.Tr key={it.type}>
                    <Table.Td>{it.type}</Table.Td>
                    <Table.Td ta="right">{it.count}</Table.Td>
                  </Table.Tr>
                ))}
                {summary.topIssues.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={2}>
                      <Text c="dimmed">No flagged issue data yet.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
